import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, MapPin, DoorClosed, Loader2, KeyRound, Car, FileDown, Map, Sheet, FilePlus, RefreshCw, Copy, Trash2, QrCode, ClipboardList, Navigation, ExternalLink, StickyNote, Receipt, FileText, CheckCircle2, Circle, Lock, Users2, X } from "lucide-react";
import { buildingsApi, doorsApi, exportsApi, savedReportsApi, clientsApi } from "@/services/api";
import { downloadBase64Envelope } from "@/lib/download";
import { useAsync } from "@/hooks/useAsync";
import { apiError, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import { buildBuildingAddress, directionsUrl } from "@/lib/directions";
import PageHeader from "@/components/PageHeader";
import BuildingCompleteDialog from "@/components/BuildingCompleteDialog";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PhotoCapture } from "@/components/PhotoCapture";

const FREQ = [
  { v: "3_month", l: "Every 3 months" },
  { v: "6_month", l: "Every 6 months" },
  { v: "12_month", l: "Every 12 months" },
];

function NewDoorDialog({ buildingId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState("");
  const [form, setForm] = useState({
    location: "",
    door_type: "communal",
    inspection_frequency: "3_month",
    door_leaf_type: "single",
    floor: "",
    apartment_number: "",
    fd_rating: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location.trim()) {
      toast.error("Location is required.");
      return;
    }
    if (!photo) {
      toast.error("Please add a door photo.");
      return;
    }
    setSaving(true);
    try {
      const res = await doorsApi.create({ building_id: buildingId, ...form, door_photo: photo });
      toast.success("Door added");
      setOpen(false);
      setPhoto("");
      onCreated?.(res.data);
    } catch (err) {
      toast.error(apiError(err, "Could not add door"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-door-btn">
          <Plus className="mr-2 h-4 w-4" /> Add door
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add door</DialogTitle>
          <DialogDescription>Register a new fire door on this building.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="d-location">Location *</Label>
            <Input id="d-location" value={form.location} onChange={set("location")} placeholder="e.g. Ground floor lobby / Flat 12" data-testid="door-location-input" />
          </div>
          <div className="space-y-2">
            <Label>Door photo *</Label>
            <PhotoCapture photos={photo ? [photo] : []} onChange={(ph) => setPhoto(ph[0] || "")} max={1} testId="door-photo" />
            <p className="text-xs text-slate-500">Take a photo of the door for identification.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Door type</Label>
              <Select value={form.door_type} onValueChange={(v) => setForm((f) => ({ ...f, door_type: v, inspection_frequency: v === "communal" ? "3_month" : "12_month" }))}>
                <SelectTrigger data-testid="door-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="communal">Communal</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leaf</Label>
              <Select value={form.door_leaf_type} onValueChange={setSel("door_leaf_type")}>
                <SelectTrigger data-testid="door-leaf-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="double">Double</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Inspection frequency</Label>
              <Select value={form.inspection_frequency} onValueChange={setSel("inspection_frequency")}>
                <SelectTrigger data-testid="door-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQ.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="d-fd">FD rating</Label>
              <Input id="d-fd" value={form.fd_rating} onChange={set("fd_rating")} placeholder="FD30 / FD60…" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="d-floor">Floor</Label>
              <Input id="d-floor" value={form.floor} onChange={set("floor")} placeholder="G / 1 / 2…" />
            </div>
            {form.door_type === "apartment" && (
              <div className="space-y-2">
                <Label htmlFor="d-apt">Apartment no.</Label>
                <Input id="d-apt" value={form.apartment_number} onChange={set("apartment_number")} placeholder="e.g. 12" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="door-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Add door
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function GenerateReportButton({ buildingId }) {
  const navigate = useNavigate();
  return (
    <Button variant="outline" onClick={() => navigate(`/reports/build?mode=building&buildingId=${buildingId}`)} data-testid="generate-building-report-btn">
      <FileDown className="mr-2 h-4 w-4" /> Generate report
    </Button>
  );
}

function SyncImportsButton({ buildingId, onSynced }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await buildingsApi.resyncImports(buildingId);
      if (data.doors_updated > 0) toast.success(`Synced ${data.doors_updated} door${data.doors_updated === 1 ? "" : "s"} from imported reports`);
      else toast.message("No imported doors to sync for this building");
      onSynced?.();
    } catch (e) { toast.error(apiError(e, "Could not sync from imports")); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" disabled={busy} onClick={run} data-testid="building-sync-imports-btn">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sync from imports
    </Button>
  );
}



function base64ToBlobUrl(b64) {
  const clean = String(b64).replace(/^data:application\/pdf;base64,/, "");
  const chars = atob(clean);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

const REPORT_TYPE_LABEL = {
  building_summary: "Full building report",
  door_inspection: "Door report",
  inspection: "Inspection report",
  quote: "Quote",
};

// Lists the reports saved against this building (newest first, with the
// full building report pinned to the top) so an inspector can re-open a
// generated report straight from the building page.
function SavedReportsMenu({ buildingId }) {
  const { data, loading, reload } = useAsync(
    () => savedReportsApi.list({ building_id: buildingId }),
    [buildingId],
  );
  const [busy, setBusy] = useState("");

  const reports = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return [...list].sort((a, b) => {
      const ap = a.report_type === "building_summary" ? 0 : 1;
      const bp = b.report_type === "building_summary" ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return new Date(b.generated_at) - new Date(a.generated_at);
    });
  }, [data]);

  const open = async (r) => {
    setBusy(r.id);
    try {
      const res = await savedReportsApi.download(r.id);
      const { pdf_base64 } = res.data || {};
      if (!pdf_base64) throw new Error("This report has no PDF stored.");
      window.open(base64ToBlobUrl(pdf_base64), "_blank", "noopener");
    } catch (e) {
      toast.error(apiError(e, "Could not open report"));
    } finally {
      setBusy("");
    }
  };

  const count = reports.length;
  return (
    <DropdownMenu onOpenChange={(o) => { if (o) reload(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="building-reports-trigger">
          <FileText className="mr-2 h-4 w-4" /> Reports
          {count > 0 && (
            <Badge className="ml-2" variant="secondary" data-testid="building-reports-count">{count}</Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-72 overflow-y-auto">
        {loading && <DropdownMenuItem disabled>Loading…</DropdownMenuItem>}
        {!loading && count === 0 && (
          <DropdownMenuItem disabled data-testid="building-reports-empty">
            No saved reports yet — generate one
          </DropdownMenuItem>
        )}
        {!loading && reports.map((r) => (
          <DropdownMenuItem
            key={r.id}
            onSelect={(e) => { e.preventDefault(); open(r); }}
            data-testid={`building-report-${r.id}`}
            className="flex flex-col items-start gap-0.5"
          >
            <span className="flex w-full items-center gap-2 font-medium">
              {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 text-primary" />}
              {REPORT_TYPE_LABEL[r.report_type] || "Report"}
            </span>
            <span className="w-full truncate text-xs text-slate-500">
              {[r.title, fmtDate(r.generated_at)].filter(Boolean).join(" · ")}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ExportMenu({ buildingId }) {
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      const res = kind === "doors" ? await exportsApi.buildingDoorsXlsx(buildingId) : await exportsApi.buildingInspectionsXlsx(buildingId);
      downloadBase64Envelope(res.data, `building-${kind}.xlsx`);
      toast.success("Export ready");
    } catch (e) { toast.error(apiError(e, "Could not export")); }
    finally { setBusy(""); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="building-export-trigger">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sheet className="mr-2 h-4 w-4" />} Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => run("doors")} data-testid="export-doors">Doors (Excel)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run("inspections")} data-testid="export-inspections">Inspections (Excel)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DuplicateDoorsDialog({ buildingId, open, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [keepMap, setKeepMap] = useState({});   // reference -> kept door id
  const [skip, setSkip] = useState({});          // reference -> true (not duplicates)
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    buildingsApi.duplicateDoors(buildingId)
      .then(({ data }) => {
        const gs = data.groups || [];
        setGroups(gs);
        const km = {};
        gs.forEach((g) => { km[g.reference] = g.suggested_keep_id; });
        setKeepMap(km);
        setSkip({});
      })
      .catch((e) => toast.error(apiError(e, "Could not load duplicates")))
      .finally(() => setLoading(false));
  }, [open, buildingId]);

  const idsToRemove = useMemo(() => {
    const ids = [];
    groups.forEach((g) => {
      if (skip[g.reference]) return;
      const keptId = keepMap[g.reference];
      g.doors.forEach((d) => { if (d.id !== keptId) ids.push(d.id); });
    });
    return ids;
  }, [groups, keepMap, skip]);

  const run = async () => {
    if (!idsToRemove.length) return;
    setSaving(true);
    try {
      const { data } = await buildingsApi.removeDuplicateDoors(buildingId, idsToRemove);
      toast.success(`Removed ${data.removed} duplicate door${data.removed === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not remove duplicates")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="duplicate-doors-dialog">
        <DialogHeader>
          <DialogTitle>Remove duplicate doors</DialogTitle>
          <DialogDescription>
            For each reference, choose the copy to keep — the others (and their inspection history) will be deleted. We keep the copy with the most history by default.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-red-600" /></div>
        ) : groups.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No duplicate doors found.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const skipped = !!skip[g.reference];
              return (
                <div key={g.reference} className={`rounded-lg border p-3 ${skipped ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-200"}`} data-testid={`dup-group-${g.reference}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-semibold text-slate-800">{g.reference} <span className="text-xs font-normal text-slate-400">({g.doors.length} copies)</span></span>
                    <button
                      type="button"
                      onClick={() => setSkip((s) => ({ ...s, [g.reference]: !s[g.reference] }))}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${skipped ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500"}`}
                      data-testid={`dup-skip-${g.reference}`}
                    >{skipped ? "Kept all" : "Not duplicates"}</button>
                  </div>
                  <div className="space-y-1.5">
                    {g.doors.map((d) => {
                      const kept = keepMap[g.reference] === d.id;
                      const willRemove = !skipped && !kept;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={skipped}
                          onClick={() => setKeepMap((m) => ({ ...m, [g.reference]: d.id }))}
                          className={`flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left ${kept && !skipped ? "border-emerald-400 bg-emerald-50" : willRemove ? "border-red-200 bg-red-50/50" : "border-slate-200"}`}
                          data-testid={`dup-door-${d.id}`}
                        >
                          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${kept && !skipped ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                            {kept && !skipped && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-700">
                              {d.location || "—"}
                              {d.last_inspection_status && <Badge variant="outline" className={`ml-2 ${statusTone(d.last_inspection_status)}`}>{prettyStatus(d.last_inspection_status)}</Badge>}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {d.inspection_count} inspection{d.inspection_count === 1 ? "" : "s"}
                              {d.last_inspection_date ? ` · last ${fmtDate(d.last_inspection_date)}` : ""}
                              {` · added ${fmtDate(d.created_at)}`}
                            </p>
                          </div>
                          <span className={`shrink-0 text-[11px] font-semibold ${skipped ? "text-slate-400" : kept ? "text-emerald-600" : "text-red-600"}`}>
                            {skipped ? "keep" : kept ? "Keep" : "Remove"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={run} disabled={saving || idsToRemove.length === 0} className="bg-red-600 hover:bg-red-700" data-testid="dup-remove-confirm">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Remove {idsToRemove.length} door{idsToRemove.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DUE_LEAD_DAYS = 21;
function dueMeta(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  if (isNaN(due)) return null;
  const days = Math.round((due - new Date()) / 86400000);
  if (days < 0) return { overdue: true, label: "Overdue", cls: "bg-red-100 text-red-700 border-red-200" };
  if (days <= DUE_LEAD_DAYS) return { dueSoon: true, label: `Due in ${days}d`, cls: "bg-amber-100 text-amber-800 border-amber-200" };
  return null;
}

function PmBadge({ door, clientMap }) {
  if (door.is_private) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-slate-800 px-1.5 py-0.5 text-white" data-testid={`door-pm-private-${door.id}`}>
        <Lock className="h-3 w-3" /> Private
      </span>
    );
  }
  const name = door.client_id ? clientMap[door.client_id] : null;
  if (name) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-1.5 py-0.5 text-blue-700" data-testid={`door-client-${door.id}`}>
        <Users2 className="h-3 w-3" /> {name}
      </span>
    );
  }
  return null;
}

function AssignManagementBar({ count, clientList, onAssignClient, onPrivate, onClear, onCancel, busy }) {
  return (
    <div className="sticky top-2 z-20 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3 shadow-sm" data-testid="assign-management-bar">
      <p className="text-sm font-semibold text-blue-900" data-testid="assign-selected-count">
        {count} door{count === 1 ? "" : "s"} selected
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value="" onValueChange={onAssignClient} disabled={busy || count === 0}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="assign-client-select"><SelectValue placeholder="Assign to client…" /></SelectTrigger>
          <SelectContent>
            {clientList.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-slate-400">No clients yet — add in Clients</div>
            ) : clientList.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`assign-client-option-${c.id}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="bg-white" disabled={busy || count === 0} onClick={onPrivate} data-testid="assign-private-btn">
          <Lock className="mr-1.5 h-4 w-4" /> Mark private
        </Button>
        <Button variant="outline" size="sm" className="bg-white" disabled={busy || count === 0} onClick={onClear} data-testid="assign-clear-btn">
          Clear assignment
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="assign-cancel-btn">
          <X className="mr-1.5 h-4 w-4" /> Cancel
        </Button>
      </div>
    </div>
  );
}

const PM_FILTERS_STATIC = [
  { v: "all", l: "All doors" },
  { v: "unassigned", l: "Unassigned" },
  { v: "private", l: "Private" },
];

function BuildingClientCard({ building, clientList, onChanged }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(building.client_id || "none");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(building.client_id || "none"); }, [building.client_id]);

  const save = async () => {
    setBusy(true);
    try {
      await buildingsApi.update(building.id, { client_id: val === "none" ? null : val });
      toast.success("Building client updated");
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(apiError(e, "Could not update client")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4" data-testid="building-client-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Client</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-700">
            <Users2 className="h-4 w-4 shrink-0 text-slate-400" /> <span className="truncate">{building.client_name || "Unassigned"}</span>
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="building-assign-client-btn">
          {building.client_id ? "Change" : "Assign"}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign managing client</DialogTitle>
            <DialogDescription>Set the client (managing agent) for this whole building. Individual doors can still be assigned to a different client for mixed-use blocks.</DialogDescription>
          </DialogHeader>
          <Select value={val} onValueChange={setVal}>
            <SelectTrigger data-testid="building-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="building-client-none">Unassigned</SelectItem>
              {clientList.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`building-client-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="building-client-save">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function BuildingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const building = useAsync(() => buildingsApi.getOne(id), [id]);
  const doors = useAsync(() => doorsApi.getAll(id), [id]);
  const clients = useAsync(() => clientsApi.list(), []);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [pmFilter, setPmFilter] = useState("all");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [assignBusy, setAssignBusy] = useState(false);

  const b = building.data;
  const doorList = Array.isArray(doors.data) ? doors.data : [];
  const clientList = Array.isArray(clients.data) ? clients.data : [];
  const clientMap = useMemo(() => {
    const m = {};
    clientList.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [clientList]);

  const filteredDoors = useMemo(() => {
    if (pmFilter === "all") return doorList;
    if (pmFilter === "unassigned") return doorList.filter((d) => !d.client_id && !d.is_private);
    if (pmFilter === "private") return doorList.filter((d) => d.is_private);
    return doorList.filter((d) => d.client_id === pmFilter);
  }, [doorList, pmFilter]);

  const toggleSel = (doorId) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(doorId)) next.delete(doorId); else next.add(doorId);
    return next;
  });
  const exitSelect = () => { setSelected(new Set()); setSelectMode(false); };

  const doAssign = async ({ client_id = null, is_private = false }) => {
    const ids = Array.from(selected);
    if (!ids.length) { toast.error("Select at least one door first"); return; }
    setAssignBusy(true);
    try {
      const { data } = await buildingsApi.assignClient(id, {
        door_ids: ids, client_id, is_private,
      });
      toast.success(`Updated ${data.modified} door${data.modified === 1 ? "" : "s"}`);
      exitSelect();
      doors.reload();
    } catch (e) {
      toast.error(apiError(e, "Could not update doors"));
    } finally {
      setAssignBusy(false);
    }
  };

  // Client-side duplicate detection (same reference, case/space-insensitive)
  // just to decide whether to nudge — the dialog fetches the rich detail.
  const dupInfo = useMemo(() => {
    const map = {};
    for (const d of doorList) {
      const ref = String(d.door_reference || "").trim().toLowerCase();
      if (!ref) continue;
      map[ref] = (map[ref] || 0) + 1;
    }
    let removable = 0;
    let groupCount = 0;
    for (const k in map) {
      if (map[k] > 1) { groupCount += 1; removable += map[k] - 1; }
    }
    return { removable, groupCount };
  }, [doorList]);

  return (
    <div>
      <Link to="/buildings" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Buildings
      </Link>

      {building.loading && <LoadingState label="Loading building…" />}
      {!building.loading && building.error && <ErrorState message={building.error} onRetry={building.reload} />}

      {!building.loading && !building.error && b && (
        <>
          <PageHeader
            title={b.name}
            subtitle={[b.address, b.postcode].filter(Boolean).join(", ")}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/buildings/${id}/plans`}>
                  <Button variant="outline" data-testid="building-plans-btn"><Map className="mr-2 h-4 w-4" /> Floor plans</Button>
                </Link>
                <Link to={`/quotes/new?mode=building&building=${id}`}>
                  <Button variant="outline" data-testid="building-quote-btn"><FilePlus className="mr-2 h-4 w-4" /> Quote building</Button>
                </Link>
                <ExportMenu buildingId={id} />
                <SyncImportsButton buildingId={id} onSynced={() => doors.reload()} />
                <Button variant="outline" onClick={() => navigate(`/buildings/${id}/remedial`)} data-testid="building-remedial-btn">
                  <ClipboardList className="mr-2 h-4 w-4" /> Remedial schedule
                </Button>
                <Button variant="outline" onClick={() => navigate(`/buildings/${id}/labels`)} data-testid="building-labels-btn">
                  <QrCode className="mr-2 h-4 w-4" /> QR labels
                </Button>
                <GenerateReportButton buildingId={id} />
                <SavedReportsMenu buildingId={id} />
                <Button variant="outline" onClick={() => setCompleteOpen(true)} data-testid="building-complete-btn">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Mark complete
                </Button>
                <NewDoorDialog buildingId={id} onCreated={() => doors.reload()} />
              </div>
            }
            testId="building-detail-header"
          />

          {(() => {
            const addr = buildBuildingAddress(b);
            const dir = directionsUrl(addr);
            if (!addr) return null;
            return (
              <a
                href={dir}
                target="_blank"
                rel="noreferrer"
                className="group mb-6 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                data-testid="building-directions"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                  <Navigation className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 group-hover:underline">Directions</p>
                  <p className="text-sm text-slate-500">{addr}</p>
                </div>
                <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
              </a>
            );
          })()}

          {(() => {
            const soon = doorList.filter((d) => dueMeta(d.next_inspection_date));
            if (!soon.length) return null;
            const overdue = soon.filter((d) => dueMeta(d.next_inspection_date)?.overdue).length;
            const dates = soon.map((d) => new Date(d.next_inspection_date)).filter((x) => !isNaN(x));
            const earliest = dates.length ? new Date(Math.min(...dates)) : null;
            return (
              <div className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${overdue ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`} data-testid="building-due-banner">
                <ClipboardList className={`mt-0.5 h-5 w-5 shrink-0 ${overdue ? "text-red-600" : "text-amber-600"}`} />
                <div className="text-sm">
                  <p className={`font-semibold ${overdue ? "text-red-800" : "text-amber-900"}`}>
                    {soon.length} door{soon.length === 1 ? "" : "s"} need inspecting{overdue ? ` — ${overdue} overdue` : ""}
                  </p>
                  <p className={overdue ? "text-red-700" : "text-amber-800"}>
                    Earliest due {earliest ? fmtDate(earliest.toISOString()) : "—"}. Arrange access and assign a team member.
                  </p>
                </div>
              </div>
            );
          })()}

          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BuildingClientCard building={b} clientList={clientList} onChanged={() => building.reload()} />
            {b.access_codes && (
              <Card className="p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Access</p>
                <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-700"><KeyRound className="h-4 w-4 text-slate-400" /> {b.access_codes}</p>
              </Card>
            )}
            {b.parking_details && (
              <Card className="p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Parking</p>
                <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-700"><Car className="h-4 w-4 text-slate-400" /> {b.parking_details}</p>
              </Card>
            )}
          </div>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-800">Doors ({filteredDoors.length}{filteredDoors.length !== doorList.length ? ` of ${doorList.length}` : ""})</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={pmFilter} onValueChange={setPmFilter}>
                <SelectTrigger className="h-9 w-48" data-testid="pm-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PM_FILTERS_STATIC.map((f) => (
                    <SelectItem key={f.v} value={f.v} data-testid={`pm-filter-${f.v}`}>{f.l}</SelectItem>
                  ))}
                  {clientList.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`pm-filter-${c.id}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectMode ? (
                <Button variant="outline" size="sm" onClick={exitSelect} data-testid="exit-select-btn">
                  <X className="mr-1.5 h-4 w-4" /> Done
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setSelectMode(true)} data-testid="select-doors-btn">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Select doors
                </Button>
              )}
            </div>
          </div>

          {selectMode && (
            <AssignManagementBar
              count={selected.size}
              clientList={clientList}
              busy={assignBusy}
              onAssignClient={(v) => doAssign({ client_id: v })}
              onPrivate={() => doAssign({ is_private: true })}
              onClear={() => doAssign({})}
              onCancel={exitSelect}
            />
          )}

          {dupInfo.removable > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="duplicate-doors-banner">
              <div className="flex items-start gap-2.5">
                <Copy className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900">
                    {dupInfo.removable} possible duplicate door{dupInfo.removable === 1 ? "" : "s"} found
                  </p>
                  <p className="text-amber-800">
                    {dupInfo.groupCount} door reference{dupInfo.groupCount === 1 ? "" : "s"} appear more than once — usually from importing a report twice. Review and remove the extras.
                  </p>
                </div>
              </div>
              <Button variant="outline" className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100" onClick={() => setDedupeOpen(true)} data-testid="review-duplicates-btn">
                Review &amp; remove
              </Button>
            </div>
          )}

          <DuplicateDoorsDialog
            buildingId={id}
            open={dedupeOpen}
            onOpenChange={setDedupeOpen}
            onDone={() => { setDedupeOpen(false); doors.reload(); }}
          />

          <BuildingCompleteDialog buildingId={id} open={completeOpen} onOpenChange={setCompleteOpen} />


          {doors.loading && <LoadingState label="Loading doors…" rows={2} />}
          {!doors.loading && doors.error && <ErrorState message={doors.error} onRetry={doors.reload} />}
          {!doors.loading && !doors.error && doorList.length === 0 && (
            <EmptyState
              title="No doors yet"
              message="Add the first door on this building to begin inspections."
              action={<NewDoorDialog buildingId={id} onCreated={() => doors.reload()} />}
              testId="doors-empty"
            />
          )}

          {!doors.loading && !doors.error && doorList.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="doors-grid">
              {filteredDoors.map((d) => {
                const isSel = selected.has(d.id);
                return (
                <Card
                  key={d.id}
                  className={`cursor-pointer p-4 transition-shadow hover:shadow-md ${selectMode && isSel ? "ring-2 ring-blue-500" : ""}`}
                  onClick={() => (selectMode ? toggleSel(d.id) : navigate(`/doors/${d.id}`))}
                  data-testid={`door-card-${d.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {selectMode && (
                        isSel
                          ? <CheckCircle2 className="h-4 w-4 shrink-0 text-blue-600" data-testid={`door-selected-${d.id}`} />
                          : <Circle className="h-4 w-4 shrink-0 text-slate-300" />
                      )}
                      <DoorClosed className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="truncate font-semibold text-slate-900">{d.door_reference || "Door"}</span>
                    </div>
                    {d.last_inspection_status && (
                      <Badge variant="outline" className={statusTone(d.last_inspection_status)}>
                        {prettyStatus(d.last_inspection_status)}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm text-slate-500">{d.location}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-slate-400">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">{d.door_type}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">{d.door_leaf_type}</span>
                    {d.fd_rating && <span className="rounded bg-slate-100 px-1.5 py-0.5">{d.fd_rating}</span>}
                    <PmBadge door={d} clientMap={clientMap} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-400">
                    {d.last_inspection_date && <span>Last inspected: {fmtDate(d.last_inspection_date)}</span>}
                    <span>Next due: {fmtDate(d.next_inspection_date)}</span>
                    {(() => { const m = dueMeta(d.next_inspection_date); return m ? <Badge variant="outline" className={m.cls} data-testid={`door-due-${d.id}`}>{m.label}</Badge> : null; })()}
                  </div>
                  {(d.last_inspection_notes || d.last_inspection_quote_notes) && (
                    <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                      {d.last_inspection_notes && (
                        <p className="flex items-start gap-1.5 text-xs text-slate-600" data-testid={`door-notes-${d.id}`}>
                          <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="line-clamp-4"><span className="font-semibold text-slate-500">Inspector notes: </span>{d.last_inspection_notes}</span>
                        </p>
                      )}
                      {d.last_inspection_quote_notes && (
                        <p className="flex items-start gap-1.5 text-xs text-violet-700" data-testid={`door-quote-notes-${d.id}`}>
                          <Receipt className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-400" />
                          <span className="line-clamp-4"><span className="font-semibold">Quote notes (not on report): </span>{d.last_inspection_quote_notes}</span>
                        </p>
                      )}
                    </div>
                  )}
                </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
